import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, extractError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = anonymous, object = user
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // Skip /me check while AuthCallback exchanges the session_id.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      return;
    }
    refresh();
  }, [refresh]);

  useEffect(() => {
    // Native iOS shell: bind APNs listeners + refresh the device token once logged in.
    if (user && typeof user === "object") {
      import("@/lib/nativePush").then((m) => m.isNativeIOS() && m.initNativePush()).catch(() => {});
      import("@/lib/iap").then((m) => m.initIap()).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    // Universal links → SPA routes (no-op outside the iOS shell).
    import("@/lib/nativeLinks").then((m) => m.initNativeLinks()).catch(() => {});
    import("@/lib/nativeShellGuard").then((m) => m.initNativeShellGuard()).catch(() => {});
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // 2FA path — server does NOT set auth cookies until /auth/2fa/verify.
      if (data && data.require_2fa) {
        return {
          ok: true,
          require_2fa: true,
          challenge_id: data.challenge_id,
          email_hint: data.email_hint,
        };
      }
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: extractError(e) };
    }
  };

  const verifyTwoFactor = async (challenge_id, code) => {
    try {
      const { data } = await api.post("/auth/2fa/verify", { challenge_id, code });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: extractError(e) };
    }
  };

  const register = async ({ email, password, handle, ageConfirmed, ref, refCode }) => {
    try {
      const { data } = await api.post("/auth/register", {
        email,
        password,
        handle: handle || undefined,
        age_confirmed: ageConfirmed,
        terms_accepted: !!ageConfirmed,
        ref: ref || undefined,
        ref_code: refCode || undefined,
      });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: extractError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Non-fatal: server-side session may already be gone. Clear local state anyway.
      console.warn("[auth] logout API call failed:", e);
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={useMemo(
      () => ({ user, setUser, login, verifyTwoFactor, register, logout, refresh }),
      // `login`, `register`, `logout` close over `setUser` (stable) — safe to
      // omit; `refresh` is a stable useCallback. Only `user` needs to re-trigger.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [user, refresh],
    )}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
